import { useState } from "react";
import { useParams } from "react-router-dom";
import { NavLink } from "react-router-dom";
import {
  MutationError,
  ProblemError,
  formatCurrency,
  hasGrant,
  useApi,
  usePrincipal,
} from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import { LogInteraction } from "./log-interaction";
import {
  useAddLifeEvent,
  useAddParticipation,
  useConsents,
  useWithdrawConsent,
  useEngagement,
  useHouseholdClaims,
  useHouseholdCommission,
  useHouseholdQueue,
  useHouseholdRecommendations,
  useHouseholdTimeline,
  useInteractions,
  useLifeEvents,
  useMembers,
  useOpportunities,
  useOwnership,
  usePolicies,
  useReassignOwner,
  useRenewalSchedule,
  useUpdateLifeEvent,
} from "./use-household";
import {
  LIFE_EVENT_TYPES,
  PARTICIPATION_FUNCTIONS,
  type LifeEvent,
  type Member,
  type TimelineEntry,
} from "./types";
import { Icon } from "../../ui/icons";

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
const money = (v: string) => formatCurrency(v, { currency: "TRY", locale: "tr" });

// Policy status as a calm k-status pill — active is green, expiring warm amber,
// everything else (incl. ended) stays neutral. Never red on this read-only 360.
const policyStatusClass = (status: string) =>
  status === "ACTIVE"
    ? "k-status--active"
    : status === "EXPIRING"
      ? "k-status--expiring"
      : "k-status--pending";

const metaText = { fontSize: 12.5, color: "var(--k-muted)" } as const;
const noneText = { fontSize: 13, color: "var(--k-muted)", margin: 0 } as const;
const panelTitle = { margin: "0 0 14px" } as const;
const rowIcon = { width: 28, height: 28 } as const;

// AC4 — the Household 360. The context an advisor works from and the hub the
// other surfaces link through. Read-heavy aggregation; renders the masked reads
// as-is (no new PII surface), scope-filtered by the API.
export function Household360() {
  const { id = "" } = useParams();
  const { t } = useT();
  const ownership = useOwnership(id);
  const members = useMembers(id);
  const policies = usePolicies(id);
  const interactions = useInteractions(id);
  const lifeEvents = useLifeEvents(id);
  const engagement = useEngagement(id);

  if (ownership.isLoading)
    return (
      <div className="k-loading">
        <div className="k-loading__head">
          <span className="k-loading__spinner" />
          <span className="k-loading__label">{t("hh.loading")}</span>
        </div>
      </div>
    );
  if (ownership.error || !ownership.data)
    return (
      <div className="k-note k-note--error">
        <div className="k-note__row">
          <span className="k-note__icon k-note__icon--error">
            <Icon name="alert-triangle" size={20} />
          </span>
          <p role="alert" className="k-note__desc">
            {ownership.error instanceof ProblemError
              ? ownership.error.problem.detail
              : t("hh.error")}
          </p>
        </div>
      </div>
    );

  const household = ownership.data.household;
  const memberList = members.data ?? [];

  return (
    <section className="surface hh-360">
      <header className="surface__head">
        <div>
          <p className="surface__section-label">{t("hh.heading")}</p>
          <h1 className="surface__title">{household.name}</h1>
          <p className="surface__subtitle">
            {t("hh.since")} {fmt(household.ownerSince)}
          </p>
        </div>
        <div className="surface__actions">
          {/* U2-F — the advisor name (U2-B) when present, else the raw UUID fallback. */}
          <span className="k-chip">
            <Icon name="member" size={14} />
            {t("hh.owner")}:{" "}
            <span data-testid="hh-owner">
              {household.relationshipOwnerAdvisorName ?? household.relationshipOwnerAdvisorId}
            </span>
          </span>
          <OwnershipActions id={id} />
        </div>
      </header>

      <CrossLinks id={id} />

      <div className="hh-panels">
        <section className="k-card" aria-label={t("hh.members")}>
          <h2 className="k-tablecard__title" style={panelTitle}>
            {t("hh.members")}
          </h2>
          {/* "kayıt yok" IS A CLAIM ABOUT THE HOUSEHOLD, not about the request.
              These panels tested only `length === 0`, so a failed read asserted
              the household has no policies / no members / no contact history —
              an active falsehood, and worse than showing nothing. The
              Opportunities panel already distinguished the two; these did not. */}
          {members.error ? (
            <p style={noneText}>{t("hh.panelError")}</p>
          ) : (
            memberList.length === 0 && <p style={noneText}>{t("hh.none")}</p>
          )}
          <ul className="hh-list">
            {memberList.map((m) => (
              <MemberRow key={m.id} member={m} />
            ))}
          </ul>
        </section>

        <section className="k-card" aria-label={t("hh.policies")}>
          <h2 className="k-tablecard__title" style={panelTitle}>
            {t("hh.policies")}
          </h2>
          {policies.error ? (
            <p style={noneText}>{t("hh.panelError")}</p>
          ) : (
            (policies.data?.length ?? 0) === 0 && <p style={noneText}>{t("hh.none")}</p>
          )}
          <ul className="hh-list">
            {(policies.data ?? []).map((p) => (
              <li key={p.id} data-testid="hh-policy">
                {/* ENDORSE-CANCEL-F — the row opens the policy detail (endorse / cancel). */}
                <NavLink
                  to={`/policies/${p.id}`}
                  data-testid={`hh-policy-link-${p.id}`}
                  className="hh-policy-link"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    textDecoration: "none",
                  }}
                >
                  <span className="k-stat__icon k-stat__icon--blue" style={rowIcon}>
                    <Icon name="protection" size={16} />
                  </span>
                  <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <strong style={{ color: "var(--k-ink)" }}>{p.lineCode}</strong>
                    <span style={metaText}>
                      {p.carrierId} · {t("hh.term")} {fmt(p.termStart)}–{fmt(p.termEnd)}
                    </span>
                  </span>
                  <span
                    className={`k-status ${policyStatusClass(p.status)}`}
                    style={{ marginLeft: "auto" }}
                  >
                    <span className="k-status__dot" />
                    {p.status}
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        </section>

        <OpportunitiesPanel id={id} />

        <RenewalPanel id={id} />

        <CommissionPanel id={id} />

        <LifeEventsPanel
          id={id}
          events={lifeEvents.data ?? []}
          failed={Boolean(lifeEvents.error)}
        />

        <TimelinePanel id={id} />

        <section className="k-card" aria-label={t("hh.engagement")}>
          <h2 className="k-tablecard__title" style={panelTitle}>
            {t("hh.engagement")}
          </h2>
          {engagement.data && (
            <p style={{ ...metaText, marginTop: 0 }} data-testid="hh-engagement-summary">
              {t("hh.engage.touches")}: {engagement.data.crossSellTouches30d} ·{" "}
              {t("hh.engage.lastContact")}:{" "}
              {engagement.data.lastContactAt ? fmt(engagement.data.lastContactAt) : "—"}
            </p>
          )}
          {interactions.error ? (
            <p style={noneText}>{t("hh.panelError")}</p>
          ) : (
            (interactions.data?.length ?? 0) === 0 && <p style={noneText}>{t("hh.none")}</p>
          )}
          <ul className="hh-list">
            {(interactions.data ?? []).map((i) => (
              <li key={i.id} data-testid="hh-interaction" style={{ color: "var(--k-text-70)" }}>
                {fmt(i.occurredAt)} · {i.channel}/{i.direction} ·{" "}
                {i.intents.map((n) => `${n.intentType}(${n.consentState})`).join(", ")}
              </li>
            ))}
          </ul>
        </section>
      </div>

      <LogInteraction householdId={id} members={memberList} />
    </section>
  );
}

function MemberRow({ member }: { member: Member }) {
  const { t } = useT();
  const { me } = usePrincipal();
  const consents = useConsents(member.id);
  const withdrawConsent = useWithdrawConsent(member.id);
  // The GRANTED marketing consent row itself — we need its id to withdraw, not just
  // a boolean. (The chip below still reads as a derived display state; the consent
  // read remains the single source of truth, nothing is decided in the browser.)
  const marketingConsent = (consents.data?.consents ?? []).find(
    (c) => c.purposeId === "marketing" && c.status === "GRANTED",
  );
  const marketingOk = marketingConsent !== undefined;
  // GAP-AUDIT-3 D18 — a customer saying "stop marketing to me" is a KVKK right, and
  // POST /v1/consents/:id/withdraw exists FOR staff ("staff may record a data
  // subject's request", consent:write@all, audited) — it simply had no control, so
  // the advisor hearing the request had nowhere to record it.
  //
  // Withdraw only. The matching GRANT endpoint is party-principals-only by design:
  // the customer grants their own açık rıza, and the advisor path is the
  // interaction's CAPTURED_IN_INTERACTION attestation where lawful basis is derived
  // server-side. A staff "grant" button here would be a lawful-basis shortcut, so
  // there deliberately is none. Withdrawal can only ever remove a permission.
  const canWithdrawConsent = hasGrant(me, "consent:write", "all");
  const [confirming, setConfirming] = useState(false);
  // TCKN-reveal-F — the eye is an affordance only for the granted (customer.tckn:reveal@all);
  // RBAC is UX, and the API re-enforces (step-up + audit) on the reveal call itself.
  const canReveal = hasGrant(me, "customer.tckn:reveal", "all");
  const initials = `${member.firstName?.[0] ?? ""}${member.lastName?.[0] ?? ""}`;
  return (
    <li
      data-testid="hh-member"
      style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
    >
      <span className="k-avatar">{initials}</span>
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <strong style={{ color: "var(--k-ink)" }}>
          {member.firstName} {member.lastName}
        </strong>
        <span style={metaText}>
          {member.email ?? "—"} · {member.phone ?? "—"}
        </span>
      </span>
      <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>
        {/* Marketing consent as a calm status chip — granted reads green, absent stays neutral. */}
        <span className={`k-status ${marketingOk ? "k-status--active" : "k-status--pending"}`}>
          <span className="k-status__dot" />
          {marketingOk ? t("hh.marketingOk") : t("hh.marketingNo")}
        </span>
        {marketingConsent && canWithdrawConsent && (
          /* QA round — this used to gate on globalThis.confirm?.(), which FAILS
             CLOSED and silently: where confirm is unavailable (jsdom, and any
             embedded/kiosk browser) the expression is undefined, the handler
             returns, and the button looks dead. That is precisely the defect class
             this whole audit is about, so the confirmation is an inline two-step
             instead — no global, visible in tests, and still deliberate. */
          <span className="hh-consent-withdraw">
            {!confirming ? (
              <button
                type="button"
                className="k-btn k-btn--ghost k-btn--sm"
                data-testid={`hh-withdraw-consent-${member.id}`}
                onClick={() => setConfirming(true)}
              >
                {t("hh.consentWithdraw")}
              </button>
            ) : (
              <>
                <span style={metaText} data-testid={`hh-withdraw-confirm-${member.id}`}>
                  {t("hh.consentWithdrawConfirm")}
                </span>
                <button
                  type="button"
                  className="k-btn k-btn--danger k-btn--sm"
                  data-testid={`hh-withdraw-yes-${member.id}`}
                  disabled={withdrawConsent.isPending}
                  onClick={() =>
                    withdrawConsent.mutate(marketingConsent.id, {
                      onSuccess: () => setConfirming(false),
                    })
                  }
                >
                  {t("hh.consentWithdrawYes")}
                </button>
                <button
                  type="button"
                  className="k-btn k-btn--ghost k-btn--sm"
                  data-testid={`hh-withdraw-no-${member.id}`}
                  onClick={() => setConfirming(false)}
                >
                  {t("hh.consentWithdrawNo")}
                </button>
              </>
            )}
            {withdrawConsent.error instanceof ProblemError && (
              <span role="alert" className="k-field__error" data-testid="hh-withdraw-error">
                {withdrawConsent.error.problem.detail}
              </span>
            )}
          </span>
        )}
        {canReveal && <TcknReveal partyId={member.id} />}
      </span>
    </li>
  );
}

// TCKN-reveal-F — click-to-reveal, NOT ambient. The full TCKN is never on the members read;
// the eye fetches it fresh from GET /v1/parties/:id/tckn (the API demands step-up + audits
// each reveal). If step-up is required, the ApiClient's CSA-1007 recovery re-auths and retries
// transparently. "Hide" drops the cleartext from memory so it isn't left sitting on screen.
type RevealState =
  | { kind: "hidden" }
  | { kind: "loading" }
  | { kind: "shown"; tckn: string }
  | { kind: "error" };

function TcknReveal({ partyId }: { partyId: string }) {
  const { t } = useT();
  const api = useApi();
  const [s, setS] = useState<RevealState>({ kind: "hidden" });

  async function reveal() {
    setS({ kind: "loading" });
    try {
      const res = await api.get<{ tckn: string }>(`/v1/parties/${partyId}/tckn`);
      setS({ kind: "shown", tckn: res.tckn });
    } catch {
      setS({ kind: "error" });
    }
  }

  if (s.kind === "shown") {
    return (
      <span
        className="tckn-reveal"
        style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
      >
        <span
          className="tckn-value"
          data-testid={`tckn-value-${partyId}`}
          style={{ fontWeight: 600, color: "var(--k-ink)", fontVariantNumeric: "tabular-nums" }}
        >
          {s.tckn}
        </span>
        <button
          type="button"
          className="k-btn k-btn--ghost k-btn--sm"
          data-testid={`tckn-hide-${partyId}`}
          aria-label={t("hh.tckn.hide")}
          onClick={() => setS({ kind: "hidden" })}
        >
          <Icon name="lock" size={14} />
          {t("hh.tckn.hide")}
        </button>
      </span>
    );
  }

  return (
    <span className="tckn-reveal" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <button
        type="button"
        className="k-btn k-btn--ghost k-btn--sm"
        data-testid={`tckn-reveal-${partyId}`}
        aria-label={t("hh.tckn.show")}
        disabled={s.kind === "loading"}
        onClick={reveal}
      >
        <Icon name="lock" size={14} />
        {s.kind === "loading" ? t("hh.tckn.loading") : t("hh.tckn.show")}
      </button>
      {s.kind === "error" && (
        <span
          role="alert"
          data-testid={`tckn-error-${partyId}`}
          style={{ fontSize: 12, color: "var(--k-muted)" }}
        >
          {t("hh.tckn.error")}
        </span>
      )}
    </span>
  );
}

function CrossLinks({ id }: { id: string }) {
  const { t } = useT();
  const claims = useHouseholdClaims(id);
  const queue = useHouseholdQueue(id);
  return (
    <nav
      className="hh-crosslinks-nav"
      aria-label={t("hh.crossLinks")}
      style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
    >
      <RecommendationsLink id={id} />
      <NavLink to="/claims" data-testid="xlink-claims" className="k-chip">
        <Icon name="protection" size={14} />
        {t("hh.links.claims")} ({claims.data?.length ?? 0})
      </NavLink>
      <NavLink to="/queue" data-testid="xlink-queue" className="k-chip">
        <Icon name="tasks" size={14} />
        {t("hh.links.queue")} ({queue.data?.length ?? 0})
      </NavLink>
    </nav>
  );
}

// U3 — the "Öneriler (N)" badge links to THIS household's recommendation, not the generic
// pending-responses screen (a dead-end). One → straight to /recommendations/:id; several →
// a short pick-list, each row deep-linking to its detail; zero → a neutral, unlinked count.
function RecommendationsLink({ id }: { id: string }) {
  const { t } = useT();
  const recs = useHouseholdRecommendations(id);
  const items = recs.data ?? [];
  const label = `${t("hh.links.recommendations")} (${items.length})`;

  if (items.length === 0) {
    return (
      <span
        className="k-chip"
        data-testid="xlink-recommendations"
        style={{ color: "var(--k-muted)" }}
      >
        <Icon name="suggestion" size={14} />
        {label}
      </span>
    );
  }
  if (items.length === 1) {
    return (
      <NavLink
        to={`/recommendations/${items[0]!.id}`}
        data-testid="xlink-recommendations"
        className="k-chip"
      >
        <Icon name="suggestion" size={14} />
        {label}
      </NavLink>
    );
  }
  return (
    <details className="xlink-recs" data-testid="xlink-recommendations">
      <summary className="k-chip" style={{ cursor: "pointer" }}>
        <Icon name="suggestion" size={14} />
        {label}
      </summary>
      <ul className="hh-list" style={{ marginTop: 8 }}>
        {items.map((r) => (
          <li key={r.id}>
            <NavLink
              to={`/recommendations/${r.id}`}
              data-testid={`xlink-rec-${r.id}`}
              style={{ color: "var(--k-blue-soft-ink)", textDecoration: "none" }}
            >
              {t("hh.rec.item")}
              {r.status ? ` · ${r.status}` : ""}
            </NavLink>
          </li>
        ))}
      </ul>
    </details>
  );
}

// ── CONSOLE-WIRE-3 panels + actions ──

// #260 — Zaman çizelgesi. The server-composed relationship history (interaction /
// life-event / öneri / hasar / poliçe) in ONE chronological list. Nothing is
// aggregated in the browser and nothing beyond enums + line codes is rendered. A
// CARE entry is toned as care — the compassion cue, never a sales prompt.
const TIMELINE_LABEL: Record<TimelineEntry["kind"], MessageKey> = {
  INTERACTION: "hh.tl.interaction",
  LIFE_EVENT: "hh.tl.lifeEvent",
  RECOMMENDATION: "hh.tl.recommendation",
  CLAIM: "hh.tl.claim",
  POLICY: "hh.tl.policy",
};

function TimelinePanel({ id }: { id: string }) {
  const { t } = useT();
  const q = useHouseholdTimeline(id);
  const entries = q.data ?? [];

  return (
    <section className="k-card" aria-label={t("hh.tl.title")} data-testid="hh-timeline">
      <h2 className="k-tablecard__title" style={panelTitle}>
        {t("hh.tl.title")}
      </h2>
      {q.isLoading && <p style={noneText}>{t("hh.loading")}</p>}
      {!q.isLoading && entries.length === 0 && <p style={noneText}>{t("hh.none")}</p>}
      <ul className="hh-list">
        {entries.map((e: TimelineEntry) => (
          <li
            key={`${e.kind}-${e.id}`}
            data-testid={`hh-tl-${e.id}`}
            className={e.tone === "care" ? "hh-tl-row hh-tl-row--care" : "hh-tl-row"}
          >
            <span className="hh-tl-when">{fmt(e.at)}</span>
            <span className="hh-tl-kind">{t(TIMELINE_LABEL[e.kind])}</span>
            {e.code && <span className="k-chip">{e.code}</span>}
            {e.status && <span className="hh-tl-status">{e.status}</span>}
            {e.tone === "care" && (
              <span className="k-badge k-badge--warning">{t("hh.tl.care")}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function OpportunitiesPanel({ id }: { id: string }) {
  const { t } = useT();
  const q = useOpportunities(id);
  const rows = q.data ?? [];
  return (
    <section className="k-card" aria-label={t("hh.opportunities")} data-testid="hh-opportunities">
      <h2 className="k-tablecard__title" style={panelTitle}>
        {t("hh.opportunities")}
      </h2>
      {q.error ? (
        <p style={noneText}>{t("hh.panelError")}</p>
      ) : rows.length === 0 ? (
        <p style={noneText}>{t("hh.none")}</p>
      ) : (
        <ul className="hh-list">
          {rows.map((o) => (
            <li key={o.id} data-testid="hh-opportunity" style={{ color: "var(--k-text-70)" }}>
              {t(`hh.oppType.${o.opportunityType}` as MessageKey)} · {o.status} ·{" "}
              {fmt(o.detectedAt)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RenewalPanel({ id }: { id: string }) {
  const { t } = useT();
  const q = useRenewalSchedule(id);
  const rows = q.data ?? [];
  return (
    <section className="k-card" aria-label={t("hh.renewal")} data-testid="hh-renewal">
      <h2 className="k-tablecard__title" style={panelTitle}>
        {t("hh.renewal")}
      </h2>
      {q.error ? (
        <p style={noneText}>{t("hh.panelError")}</p>
      ) : rows.length === 0 ? (
        <p style={noneText}>{t("hh.none")}</p>
      ) : (
        <ul className="hh-list">
          {rows.map((r) => (
            <li key={r.id} data-testid="hh-renewal-item" style={{ color: "var(--k-text-70)" }}>
              {fmt(r.termEnd)} · {r.currentStage} · {r.status}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CommissionPanel({ id }: { id: string }) {
  const { t } = useT();
  const q = useHouseholdCommission(id);
  const entries = q.data?.entries ?? [];
  return (
    <section className="k-card" aria-label={t("hh.commission")} data-testid="hh-commission">
      <h2 className="k-tablecard__title" style={panelTitle}>
        {t("hh.commission")}
      </h2>
      {q.error ? (
        <p style={noneText}>{t("hh.panelError")}</p>
      ) : entries.length === 0 ? (
        <p style={noneText}>{t("hh.none")}</p>
      ) : (
        <ul className="hh-list">
          {entries.map((e) => (
            <li
              key={e.id}
              data-testid="hh-commission-entry"
              style={{ color: "var(--k-ink-2)", fontVariantNumeric: "tabular-nums" }}
            >
              {money(e.expectedAmount)} / {money(e.receivedAmount)} · {t("hh.gap")}: {money(e.gap)}{" "}
              · {e.status}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function LifeEventsPanel({
  id,
  events,
  failed,
}: {
  id: string;
  events: LifeEvent[];
  /* The read's failure travels as a prop because the panel takes the rows, not
     the query — without it an unread list would claim the household has had no
     life events, which is a statement about the customer, not about the fetch. */
  failed: boolean;
}) {
  const { t } = useT();
  const add = useAddLifeEvent(id);
  const update = useUpdateLifeEvent(id);
  const [eventType, setEventType] = useState<(typeof LIFE_EVENT_TYPES)[number]>("NEW_VEHICLE");
  return (
    <section className="k-card" aria-label={t("hh.lifeEvents")}>
      <h2 className="k-tablecard__title" style={panelTitle}>
        {t("hh.lifeEvents")}
      </h2>
      {failed ? (
        <p style={noneText}>{t("hh.panelError")}</p>
      ) : (
        events.length === 0 && <p style={noneText}>{t("hh.none")}</p>
      )}
      <ul className="hh-list">
        {events.map((e) => (
          <li
            key={e.id}
            data-testid="hh-life-event"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              color: "var(--k-text-70)",
            }}
          >
            <span>
              {t(`hh.eventType.${e.eventType}` as MessageKey)} · {e.status} · {fmt(e.occurredAt)}
            </span>
            {/* CARE-hold — a calm amber notice (sales suppressed), never an alarm. */}
            {e.sensitivity === "CARE" && (
              <span className="k-badge k-badge--warning" data-testid="care-hold">
                <Icon name="lock" size={13} />
                {t("hh.careHold")}
              </span>
            )}
            {e.status !== "ACTIONED" && (
              <button
                type="button"
                className="k-btn k-btn--soft k-btn--sm"
                data-testid={`life-actioned-${e.id}`}
                disabled={update.isPending}
                onClick={() => update.mutate({ eventId: e.id, status: "ACTIONED" })}
                style={{ marginLeft: "auto" }}
              >
                {t("hh.markActioned")}
              </button>
            )}
          </li>
        ))}
      </ul>
      <div
        className="hh-life-add"
        style={{ display: "flex", gap: 10, marginTop: 14, alignItems: "flex-end" }}
      >
        <div className="k-select" style={{ flex: 1 }}>
          <select
            className="k-select__native"
            aria-label={t("hh.eventType")}
            data-testid="life-event-type"
            value={eventType}
            onChange={(e) => setEventType(e.target.value as (typeof LIFE_EVENT_TYPES)[number])}
          >
            {LIFE_EVENT_TYPES.map((et) => (
              <option key={et} value={et}>
                {t(`hh.eventType.${et}` as MessageKey)}
              </option>
            ))}
          </select>
          <span className="k-select__chevron">
            <Icon name="chevron-down" size={16} />
          </span>
        </div>
        <button
          type="button"
          className="k-btn k-btn--primary k-btn--sm"
          data-testid="life-event-add"
          disabled={add.isPending}
          onClick={() => add.mutate({ householdId: id, eventType, source: "ADVISOR_OBSERVED" })}
        >
          <Icon name="plus" size={14} />
          {t("hh.addLifeEvent")}
        </button>
      </div>
      {add.error instanceof ProblemError && (
        <p role="alert" style={{ fontSize: 12, color: "var(--k-muted)", marginTop: 10 }}>
          {add.error.problem.detail}
        </p>
      )}
      {/* `add` reported itself; `update` — the "Aksiyon alındı" tick — did not,
          two lines away from it. */}
      <MutationError
        error={update.error}
        fallback={t("hh.actionFailed")}
        offline={t("common.offline")}
      />
    </section>
  );
}

// Ownership actions. reassign-owner is admin-gated (household:reassign@all); participation
// is self-recorded by any authenticated staff (no grant gate). RBAC is UX; the API enforces.
function OwnershipActions({ id }: { id: string }) {
  const { t } = useT();
  const { me } = usePrincipal();
  const canReassign = hasGrant(me, "household:reassign", "all");
  const reassign = useReassignOwner(id);
  const participate = useAddParticipation(id);
  const [toAdvisorId, setToAdvisorId] = useState("");
  const [reason, setReason] = useState("");
  const [fn, setFn] = useState<(typeof PARTICIPATION_FUNCTIONS)[number]>("OPERATIONS");
  const [taskContext, setTaskContext] = useState("");

  return (
    <details className="hh-ownership-actions" data-testid="ownership-actions">
      <summary className="k-btn k-btn--secondary k-btn--sm" style={{ cursor: "pointer" }}>
        <Icon name="edit" size={14} />
        {t("hh.ownershipActions")}
      </summary>

      {canReassign && (
        <form
          className="hh-owner-form k-card k-card--pad-sm"
          data-testid="reassign-form"
          style={{ marginTop: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (toAdvisorId.trim() && reason.trim())
              reassign.mutate({ toAdvisorId: toAdvisorId.trim(), reason: reason.trim() });
          }}
        >
          <h3 className="k-tablecard__title" style={panelTitle}>
            {t("hh.reassign.title")}
          </h3>
          <label className="k-field">
            <span className="k-field__label">{t("hh.reassign.toAdvisor")}</span>
            <input
              className="k-input"
              data-testid="reassign-advisor"
              value={toAdvisorId}
              onChange={(e) => setToAdvisorId(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("hh.reassign.reason")}</span>
            <input
              className="k-input"
              data-testid="reassign-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="k-btn k-btn--primary k-btn--sm"
            data-testid="reassign-submit"
            disabled={reassign.isPending || !toAdvisorId.trim() || !reason.trim()}
          >
            {t("hh.reassign.submit")}
          </button>
          {reassign.error instanceof ProblemError && (
            <p role="alert" style={{ fontSize: 12, color: "var(--k-muted)", marginTop: 10 }}>
              {reassign.error.problem.detail}
            </p>
          )}
        </form>
      )}

      <form
        className="hh-owner-form k-card k-card--pad-sm"
        data-testid="participation-form"
        style={{ marginTop: 12 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (taskContext.trim())
            participate.mutate({ function: fn, taskContext: taskContext.trim() });
        }}
      >
        <h3 className="k-tablecard__title" style={panelTitle}>
          {t("hh.participation.title")}
        </h3>
        <label className="k-field">
          <span className="k-field__label">{t("hh.participation.function")}</span>
          <div className="k-select">
            <select
              className="k-select__native"
              data-testid="participation-function"
              value={fn}
              onChange={(e) => setFn(e.target.value as (typeof PARTICIPATION_FUNCTIONS)[number])}
            >
              {PARTICIPATION_FUNCTIONS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            <span className="k-select__chevron">
              <Icon name="chevron-down" size={16} />
            </span>
          </div>
        </label>
        <label className="k-field">
          <span className="k-field__label">{t("hh.participation.context")}</span>
          <input
            className="k-input"
            data-testid="participation-context"
            value={taskContext}
            onChange={(e) => setTaskContext(e.target.value)}
          />
        </label>
        <button
          type="submit"
          className="k-btn k-btn--primary k-btn--sm"
          data-testid="participation-submit"
          disabled={participate.isPending || !taskContext.trim()}
        >
          {t("hh.participation.submit")}
        </button>
        {participate.isSuccess && (
          <p
            role="status"
            data-testid="participation-ok"
            style={{ fontSize: 12, color: "var(--k-green-solid)", marginTop: 10 }}
          >
            {t("hh.participation.ok")}
          </p>
        )}
        {/* Only isSuccess was rendered, so a rejected write looked exactly like
            not having pressed the button. */}
        <MutationError
          error={participate.error}
          fallback={t("hh.actionFailed")}
          offline={t("common.offline")}
        />
      </form>
    </details>
  );
}
